import { useState } from 'react';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import { Select } from '../../../shared/components/Select';
import { Toggle } from '../../../shared/components/Toggle';
import Button from '../../../shared/components/Button';
import {
  BOOKING_MEDIUMS,
  type BookingMedium,
  type BookingService,
  type CreateBookingServicePayload,
} from '../api/bookingServicesApi';

const MEDIUM_LABELS: Record<BookingMedium, string> = {
  IN_PERSON: 'In person',
  VIDEO_CALL: 'Video call',
  PHONE: 'Phone',
  ANY: 'Any',
};

const MEDIUM_OPTIONS = BOOKING_MEDIUMS.map((m) => ({ value: m, title: MEDIUM_LABELS[m] }));

const CURRENCY_OPTIONS = [
  { value: 'NGN', title: 'NGN (₦)' },
  { value: 'USD', title: 'USD ($)' },
];

// Body fields shared by create/update — tenantId/solutionId are injected by the page.
export type BookingServiceFormValues = Omit<CreateBookingServicePayload, 'tenantId' | 'solutionId'>;

interface Props {
  initial?: BookingService | null;
  onSubmit: (values: BookingServiceFormValues) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function BookingServiceForm({ initial, onSubmit, onCancel, isSaving }: Props) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [durationMins, setDurationMins] = useState(String(initial?.durationMins ?? 60));
  const [medium, setMedium] = useState<BookingMedium>(initial?.medium ?? 'ANY');
  const [maxBookingsPerSlot, setMaxBookingsPerSlot] = useState(String(initial?.maxBookingsPerSlot ?? 1));
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [chargingEnabled, setChargingEnabled] = useState(initial?.chargingEnabled ?? false);
  // Fee is stored in kobo on the wire; displayed/entered in major units (naira).
  const [feeMajor, setFeeMajor] = useState(
    initial && initial.fee ? (initial.fee / 100).toString() : '',
  );
  const [currency, setCurrency] = useState(initial?.currency ?? 'NGN');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    const duration = parseInt(durationMins, 10);
    if (!Number.isFinite(duration) || duration < 15) {
      setError('Duration must be at least 15 minutes');
      return;
    }

    const slots = parseInt(maxBookingsPerSlot, 10);
    if (!Number.isFinite(slots) || slots < 1) {
      setError('Max bookings per slot must be at least 1');
      return;
    }

    const feeMajorNum = feeMajor.trim() === '' ? 0 : parseFloat(feeMajor);
    if (!Number.isFinite(feeMajorNum) || feeMajorNum < 0) {
      setError('Fee cannot be negative');
      return;
    }
    if (chargingEnabled && feeMajorNum <= 0) {
      setError('Enter a fee greater than zero when charging is enabled');
      return;
    }

    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      // booking-service requires a category; GENERAL is the catch-all default.
      category: initial?.category ?? 'GENERAL',
      durationMins: duration,
      medium,
      maxBookingsPerSlot: slots,
      isActive,
      chargingEnabled,
      // Convert major units → kobo on submit. Zero out fee when not charging.
      fee: chargingEnabled ? Math.round(feeMajorNum * 100) : 0,
      currency,
    });
  };

  const currencySymbol = currency === 'NGN' ? '₦' : '$';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Title"
        placeholder="e.g. Personal Styling Session"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <TextArea
        label="Description"
        placeholder="What customers get from this booking"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Duration (minutes)"
          type="number"
          min={15}
          step={5}
          value={durationMins}
          onChange={(e) => setDurationMins(e.target.value)}
        />
        <Input
          label="Max bookings per slot"
          type="number"
          min={1}
          value={maxBookingsPerSlot}
          onChange={(e) => setMaxBookingsPerSlot(e.target.value)}
        />
      </div>

      <Select
        label="Medium"
        options={MEDIUM_OPTIONS}
        value={medium}
        onChange={(v) => setMedium(v as BookingMedium)}
      />

      <div className="pt-1">
        <Toggle label="Active" checked={isActive} onChange={setIsActive} />
      </div>

      {/* Charging controls */}
      <div className="rounded-[12px] border border-[#E5E7EB] bg-muted p-4 space-y-4">
        <Toggle
          label="Charge customers for this booking"
          checked={chargingEnabled}
          onChange={setChargingEnabled}
        />
        {chargingEnabled && (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={`Fee (${currencySymbol})`}
              type="number"
              min={0}
              step="0.01"
              placeholder="0.00"
              value={feeMajor}
              onChange={(e) => setFeeMajor(e.target.value)}
            />
            <Select
              label="Currency"
              options={CURRENCY_OPTIONS}
              value={currency}
              onChange={setCurrency}
            />
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-3 pt-1">
        <Button type="button" variant="outline" size="sm" className="h-[40px]" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" className="h-[40px]" disabled={isSaving} loading={isSaving}>
          {initial ? 'Save changes' : 'Create service'}
        </Button>
      </div>
    </form>
  );
}
